// @ts-strict-ignore
import { useMemo } from 'react';
import type { JSX } from 'react';
import { IconButton, styled } from '@mui/material';
import { tss } from 'tss-react/mui';
import { getThemeColor } from '@/muiTheme';
import { prependZero } from '@/services/formatDateForAPI';
import Text from '../Text';
import { ArrowLeftIcon, ArrowRightIcon } from '../icons';

/**
 * Should the date be disabled? The date passed should be treated as if
 * it uses UTC timezone.
 */
type DisableDateFn = (utcDate: Date) => boolean;

interface MonthRangePickerProps {
    values: [fromDate: string, toDate: string];
    onChange: (value: [fromDate: string, toDate: string]) => void;
    locale?: string;
    disableFromDate?: DisableDateFn;
    disableToDate?: DisableDateFn;
    disableFromPrevYear?: boolean;
    disableToPrevYear?: boolean;
    disableFromNextYear?: boolean;
    disableToNextYear?: boolean;
}

export default function MonthRangePicker({
    values,
    onChange,
    locale,
    className,
    // For From `MonthPicker` props
    disableFromDate,
    disableFromNextYear,
    disableFromPrevYear,
    // For To `MonthPicker` props
    disableToDate,
    disableToNextYear,
    disableToPrevYear,
    ...rootProps
}: MonthRangePickerProps & Omit<JSX.IntrinsicElements['div'], 'onChange'>) {
    const { classes, cx } = useMonthRangePickerStyles();
    const [fromDate, toDate] = values;

    return (
        <div {...rootProps} className={cx(classes.monthRangePicker, className)}>
            <MonthPicker
                value={fromDate}
                onChange={(fromDate) => onChange([fromDate, toDate])}
                locale={locale}
                disabledNextYear={disableFromNextYear}
                disabledPrevYear={disableFromPrevYear}
                disableDate={disableFromDate}
            />
            <MonthPicker
                value={toDate}
                onChange={(toDate) => onChange([fromDate, toDate])}
                locale={locale}
                disabledNextYear={disableToNextYear}
                disabledPrevYear={disableToPrevYear}
                disableDate={disableToDate}
            />
        </div>
    );
}

const useMonthRangePickerStyles = tss.create(({ theme }) => ({
    monthRangePicker: {
        display: 'inline-block',
        [theme.breakpoints.up('sm')]: {
            display: 'inline-flex',
        },
    },
}));

type MonthStatus = 'disabled' | 'active' | 'notActive';

interface PickerProps {
    value: string;
    onChange: (date: string) => void;
    locale?: string;
    disabledNextYear?: boolean;
    disabledPrevYear?: boolean;
}

function MonthPicker({
    value,
    onChange,
    locale,
    disableDate,
    disabledNextYear,
    disabledPrevYear,
}: PickerProps & {
    disableDate?: DisableDateFn;
}) {
    const { classes, cx } = useMonthPickerStyles();

    // Returns an array of "Date" object representing all of the months of the current year.
    const calendarMonths: {
        id: number;
        date: string;
        toLocale: () => string;
        status: MonthStatus;
    }[] = useMemo(() => {
        const activeDate = new Date(value);
        // const currentDate = new Date(getFormattedCurrentDate());
        const months = [];
        for (let m = 1; m <= 12; m++) {
            // we will use this variable to some logic like creating `date`
            // or comparing `dates` instead of using the `locale` version of the date
            // to avoid issue like invalid `value` passed to the `Date` constructor.
            const stringifyDate = `${activeDate.getUTCFullYear()}-${`${prependZero(
                m,
            )}`}`;
            const ownDate = new Date(stringifyDate);

            let monthStatus: MonthStatus = 'notActive';
            if (disableDate?.(ownDate)) {
                monthStatus = 'disabled';
            } else if (stringifyDate === value) {
                monthStatus = 'active';
            }

            months.push({
                id: m,
                date: stringifyDate,
                toLocale() {
                    const localeMonth = Intl.DateTimeFormat(locale, {
                        month: 'short',
                        timeZone: 'UTC',
                    }).format(ownDate);
                    // finalise the string like removing "dot". E.g when formatting Swedish month
                    // using `Intl.DateTimeFormat`, some if its months has appended "dot".
                    return removeLastDot(localeMonth);
                },
                status: monthStatus,
            });
        }
        return months;
    }, [disableDate, locale, value]);

    return (
        <div className={cx(classes.monthPicker)}>
            <YearPicker
                disabledNextYear={disabledNextYear}
                disabledPrevYear={disabledPrevYear}
                value={value}
                onChange={onChange}
                locale={locale}
            />
            <Container className={classes.monthsContainer}>
                {calendarMonths.map((month) => (
                    <div key={month.id}>
                        <button
                            type="button"
                            disabled={month.status === 'disabled'}
                            className={cx(
                                classes.month,
                                classes[`month__${month.status}`],
                            )}
                            onClick={() => onChange(month.date)}
                        >
                            <Text
                                color={getMonthTextColor(month.status)}
                                className={cx(classes.monthText, {
                                    [classes.monthText__active]:
                                        month.status === 'active',
                                })}
                            >
                                {month.toLocale()}
                            </Text>
                        </button>
                    </div>
                ))}
            </Container>
        </div>
    );
}

function getMonthTextColor(monthStatus: MonthStatus) {
    switch (monthStatus) {
        case 'active': {
            return 'white';
        }
        case 'notActive': {
            return 'label';
        }
        // Else, the month is `disabled`.
        default: {
            return 'lightGray';
        }
    }
}

const useMonthPickerStyles = tss.create(({ theme }) => {
    const getColor = getThemeColor(theme);
    return {
        monthPicker: {
            width: 300,
            border: `1px solid ${getColor('lightGray')}`,
            backgroundColor: getColor('white'),
        },
        monthsContainer: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px 16px',
            justifyItems: 'center',
            padding: '0 16px',
        },
        month: {
            outline: 0,
            color: getColor('white'),
            border: 'none',
            backgroundColor: 'transparent',
            width: 50,
            height: 50,
            borderRadius: '50%',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        // eslint-disable-next-line tss-unused-classes/unused-classes
        month__notActive: {
            cursor: 'pointer',

            '&:hover, &:focus': {
                backgroundColor: getColor('lightGray'),
                transition:
                    'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            },
        },
        // eslint-disable-next-line tss-unused-classes/unused-classes
        month__active: {
            cursor: 'pointer',
            backgroundColor: getColor('primary'),
        },
        monthText: {
            textTransform: 'capitalize',
            fontSize: 14,
            fontFamily: 'AvertaStandard, sans-serif',
        },
        monthText__active: {
            fontSize: 16,
        },
    };
});

function YearPicker({
    value,
    onChange,
    locale,
    disabledNextYear = false,
    disabledPrevYear = false,
}: PickerProps) {
    const { classes } = useYearPickerStyles();
    const activeDate = new Date(value);
    const localeYear = Intl.DateTimeFormat(locale, {
        year: 'numeric',
    }).format(activeDate);
    const yearNumber = activeDate.getUTCFullYear();

    function getIconColor(disabled: boolean) {
        return disabled ? 'lightGray' : 'black';
    }

    function changeYearPickerHandlerWrapper(year: number) {
        return function handleChange() {
            const stringifyNewDate = `${year}-${prependZero(
                activeDate.getUTCMonth() + 1,
            )}`;
            onChange(stringifyNewDate);
        };
    }

    return (
        <Container className={classes.yearsContainer}>
            <div className={classes.line} />
            <div>
                <IconButton
                    aria-label="Prev year"
                    onClick={changeYearPickerHandlerWrapper(yearNumber - 1)}
                    disabled={disabledPrevYear}
                    size="large"
                >
                    <ArrowLeftIcon
                        size={14}
                        color={getIconColor(disabledPrevYear)}
                    />
                </IconButton>
                <Text
                    fontWeight={600}
                    textTransform="uppercase"
                    className={classes.yearText}
                    aria-label="Current year"
                >
                    {localeYear}
                </Text>
                <IconButton
                    aria-label="Next year"
                    onClick={changeYearPickerHandlerWrapper(yearNumber + 1)}
                    disabled={disabledNextYear}
                    size="large"
                >
                    <ArrowRightIcon
                        size={14}
                        color={getIconColor(disabledNextYear)}
                    />
                </IconButton>
            </div>
            <div className={classes.line} />
        </Container>
    );
}

const useYearPickerStyles = tss.create(({ theme }) => {
    const getColor = getThemeColor(theme);
    return {
        yearsContainer: {
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            marginBottom: 16,
        },
        line: {
            backgroundColor: getColor('lightGray'),
            height: 2,
        },
        yearText: {
            margin: '0 4px',
            verticalAlign: 'middle',
            fontFamily: 'AvertaStandard, sans-serif',
            fontSize: 16,
        },
    };
});

const Container = styled('div')(() => ({
    margin: '16px 0',
}));

function removeLastDot(string) {
    return string[string.length - 1] === '.'
        ? string.slice(0, string.length - 1)
        : string;
}
